import { Checkbox } from '@mantine/core';
import type { FloatingPosition } from '@mantine/core/lib/Floating';
import type { DateValue } from '@mantine/dates';
import {
	useUpdateEntityCustomProperty,
	type CustomPropertyOut,
} from '@repo/api-codegen';
import { useCallback } from 'react';
import type { ISecodaEntity } from '../api';
import { ResourceCustomPropertySelector } from './Properties/ResourceCustomPropertySelector';
import {
	DatePickerRender,
	DescriptionRender,
	NumberRender,
	OwnerRender,
	TagRender,
} from './TableV2/render';

interface CustomPropertyRendererV2Props {
	customProperty: CustomPropertyOut;
	entity: ISecodaEntity;
	hideOnEmpty?: boolean;
	readOnly?: boolean;
	disableHover?: boolean;
	position?: FloatingPosition;
	withinPortal?: boolean;
}

export function CustomPropertyRendererV2({
	customProperty,
	entity,
	hideOnEmpty = true,
	readOnly = false,
	disableHover = false,
	position,
	withinPortal,
}: CustomPropertyRendererV2Props) {
	const { mutateAsync: updateEntityCustomProperty } =
		useUpdateEntityCustomProperty({});

	const onChange = useCallback(
		(entityId: string) =>
			(value: string | string[] | boolean | undefined | number | DateValue) => {
				updateEntityCustomProperty({
					pathParams: {
						customPropertyId: customProperty.id,
						entityId: entityId,
					},
					body: {
						value: typeof value === 'string' ? value : JSON.stringify(value),
					},
				});
			},
		[updateEntityCustomProperty, customProperty.id]
	);

	switch (customProperty.value_type) {
		case 'select':
			return (
				<TagRender
					key={customProperty.id}
					label={customProperty.name}
					record={entity}
					field={`properties2.${customProperty.id}`}
					onChange={onChange}
					hideOnEmpty={hideOnEmpty}
					customPropertyIdFilter={customProperty.id}
					readOnly={readOnly}
					position={position}
					withinPortal={withinPortal}
				/>
			);
		case 'tag':
			return (
				<TagRender
					key={customProperty.id}
					label={customProperty.name}
					record={entity}
					field={`properties2.${customProperty.id}`}
					onChange={onChange}
					hideOnEmpty={hideOnEmpty}
					readOnly={readOnly}
					position={position}
					withinPortal={withinPortal}
				/>
			);
		case 'user':
			return (
				<OwnerRender
					key={customProperty.id}
					record={entity}
					onChangeUserOwners={onChange}
					label={customProperty.name}
					field={`properties2.${customProperty.id}`}
					hideOnEmpty={hideOnEmpty}
					forceReadOnly={readOnly}
					position={position}
					withinPortal={withinPortal}
				/>
			);
		case 'resource':
			return (
				<ResourceCustomPropertySelector
					entity={entity}
					customProperty={customProperty}
					field={`properties2.${customProperty.id}`}
					onChange={onChange}
					readOnly={readOnly}
					position={position}
					withinPortal={withinPortal}
				/>
			);
		case 'date':
			return (
				<DatePickerRender
					label={customProperty.name}
					record={entity}
					field={`properties2.${customProperty.id}`}
					onChange={onChange}
					readOnly={readOnly}
				/>
			);

		case 'number':
			return (
				<NumberRender
					label={customProperty.name}
					record={entity}
					field={`properties2.${customProperty.id}`}
					onChange={onChange}
					readOnly={readOnly}
				/>
			);

		case 'boolean':
			return (
				<Checkbox
					key={customProperty.id}
					defaultChecked={entity?.properties2?.[customProperty.id] === 'true'}
					onChange={(event) => {
						onChange(entity.id)(Boolean(event.target.checked));
					}}
					disabled={readOnly}
					label={hideOnEmpty ? '' : customProperty.name}
				/>
			);
		default:
			return (
				<DescriptionRender
					key={customProperty.id}
					record={entity}
					label={customProperty.name}
					field={`properties2.${customProperty.id}`}
					onChange={onChange}
					readOnly={readOnly}
					disableHover={disableHover}
				/>
			);
	}
}
